import { NavLink } from "react-router-dom";
import {
  CheckCheck,
  LayoutDashboard,
  ClipboardCheck,
  Stethoscope,
  Map,
  Wallet,
  CalendarDays,
  MapPinned,
  BarChart3,
  PackageOpen,
  Pill,
  Truck,
  Users2,
  Settings,
  BookOpen,
  BookUser,
  LogOut,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { usePendingApprovals } from "../context/PendingApprovalsContext";
import { CallistoLogo } from "./CallistoLogo";

export const SIDEBAR_ITEMS = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["sales_officer", "assistant_manager", "admin_manager", "admin", "director"] },
  // Not shown to sales_officer: they approve nothing, so the page would always
  // be empty for them. The queue itself is scoped by the backend regardless —
  // this is a nav decision, not an access control.
  { to: "/approvals", label: "Approvals", icon: CheckCheck, badge: "approvals", roles: ["assistant_manager", "admin_manager", "admin", "director"] },
  { to: "/attendance", label: "Attendance", icon: ClipboardCheck, roles: ["sales_officer", "assistant_manager", "admin_manager", "admin", "director"] },
  { to: "/doctors", label: "Doctors", icon: BookUser, roles: ["sales_officer", "assistant_manager", "admin_manager", "admin", "director"] },
  { to: "/doctor-visits", label: "Doctor Visits", icon: Stethoscope, roles: ["sales_officer", "assistant_manager", "admin_manager", "admin", "director"] },
  { to: "/route-planner", label: "Route Planner", icon: Map, roles: ["sales_officer", "assistant_manager", "admin_manager", "admin", "director"] },
  { to: "/expenses", label: "Expenses", icon: Wallet, roles: ["sales_officer", "assistant_manager", "admin_manager", "admin", "director"] },
  { to: "/leaves", label: "Leave Management", icon: CalendarDays, roles: ["sales_officer", "assistant_manager", "admin_manager", "admin", "director"] },
  { to: "/field-map", label: "Field Map", icon: MapPinned, roles: ["assistant_manager", "admin_manager", "admin", "director"] },
  { to: "/analytics", label: "Analytics", icon: BarChart3, roles: ["sales_officer", "assistant_manager", "admin_manager", "admin", "director"] },
  { to: "/samples", label: "Sample Log", icon: PackageOpen, roles: ["sales_officer", "assistant_manager", "admin_manager", "admin", "director"] },
  { to: "/distributors", label: "Distributors", icon: Truck, roles: ["sales_officer", "assistant_manager", "admin_manager", "admin", "director"] },
  { to: "/products", label: "Products", icon: Pill, roles: ["admin_manager", "admin", "director"] },
  { to: "/territories", label: "Territories", icon: Map, roles: ["admin_manager", "admin", "director"] },
  // Both manager tiers manage teams since #15 (induct / reassign / release).
  // What each role can DO on the page is gated there; this only opens the door.
  { to: "/team", label: "Team", icon: Users2, roles: ["admin", "director", "admin_manager", "assistant_manager"] },
  { to: "/settings", label: "Settings", icon: Settings, roles: ["admin", "director"] },
  { to: "/user-guide", label: "User Guide", icon: BookOpen, roles: ["sales_officer", "assistant_manager", "admin_manager", "admin", "director"] },
];

const ROLE_LABEL = {
  sales_officer: "Sales Officer",
  assistant_manager: "Sales Manager",
  admin_manager: "Admin Manager",
  admin: "Tech Admin",
  director: "Director",
};

export default function Sidebar({ onNavigate }) {
  const { user, logout } = useAuth();
  const { total: pendingApprovals } = usePendingApprovals();
  const role = user?.role;
  const items = SIDEBAR_ITEMS.filter((i) => i.roles.includes(role));

  return (
    <aside
      className="h-full w-72 bg-callisto-navy text-white flex flex-col callisto-brand-grid"
      data-testid="app-shell-sidebar"
    >
      {/* THE TOP OF THIS PANEL IS THE TOP OF THE SCREEN. On mobile the sidebar
          is rendered into a `fixed inset-y-0 left-0` Sheet, so without the
          status-bar inset its first nav links — Dashboard, Approvals — are
          drawn behind the clock/battery row and every tap on them is taken by
          the OS, not the app. That is the "tabs don't respond" half of the
          report. */}
      <div className="px-5 py-5 pt-[calc(1.25rem+var(--safe-area-top))] border-b border-white/10">
        <CallistoLogo variant="dark" size="md" />
        <p className="mt-2 text-[11px] uppercase tracking-[0.2em] text-callisto-gold/90">
          Sales Force Automation
        </p>
      </div>

      <nav className="flex-1 overflow-y-auto py-3 px-2">
        <ul className="space-y-0.5">
          {items.map(({ to, label, icon: Icon, badge }) => (
            <li key={to}>
              <NavLink
                to={to}
                onClick={onNavigate}
                end={to === "/dashboard"}
                className={({ isActive }) =>
                  `sidebar-link flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors ${
                    isActive
                      ? "bg-white/10 text-callisto-gold font-semibold"
                      : "text-white/80 hover:bg-white/5 hover:text-white"
                  }`
                }
                data-active="false"
                data-testid={`sidebar-nav-link-${to.replace("/", "")}`}
              >
                {({ isActive }) => (
                  <>
                    <Icon
                      className={`h-4.5 w-4.5 shrink-0 ${isActive ? "text-callisto-gold" : "text-white/70"}`}
                      strokeWidth={2}
                      size={18}
                    />
                    <span>{label}</span>
                    {badge === "approvals" && pendingApprovals > 0 && (
                      <span
                        className="ml-auto min-w-[1.25rem] px-1.5 py-0.5 rounded-full bg-callisto-gold text-callisto-navy text-[10px] font-bold text-center tabular-nums"
                        data-testid="sidebar-approvals-badge"
                        aria-label={`${pendingApprovals} awaiting your approval`}
                      >
                        {pendingApprovals > 99 ? "99+" : pendingApprovals}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* `p-4 pb-safe` set padding-bottom TO the inset instead of adding it,
          so this block had no bottom padding at all on any screen without a
          notch. The sum is spelled out; see the note on .pb-safe. */}
      <div className="border-t border-white/10 p-4 pb-[calc(1rem+var(--safe-area-bottom))]">
        <div className="mb-3">
          <p className="text-sm font-semibold text-white truncate" data-testid="sidebar-user-name">
            {user?.full_name}
          </p>
          <p className="text-xs text-callisto-gold/90 mt-0.5" data-testid="sidebar-user-role">
            {ROLE_LABEL[role] || role}
          </p>
        </div>
        <button
          type="button"
          onClick={logout}
          className="w-full flex items-center justify-center gap-2 rounded-md bg-white/5 hover:bg-callisto-gold hover:text-callisto-navy px-3 py-2 text-sm font-medium text-white transition-colors"
          data-testid="logout-button"
        >
          <LogOut size={16} />
          Log out
        </button>
      </div>
    </aside>
  );
}
